import { inject, Injectable } from '@angular/core';
import { InsightApiService } from '@marmon/frontend/krista';
import {
	Insight,
	isPendingInsight,
	isPollableInsight,
} from '@marmon/shared/types/krista';
import { map, Observable, of, switchMap } from 'rxjs';
import { followInsight } from './insight-follow';

/**
 * Submits a whole-analysis summary and follows it until Krista finishes.
 * `load` only reads the cache. A pending insight that already has a
 * Krista event id is polled again.
 */
@Injectable({ providedIn: 'root' })
export class AnalysisSummaryInsightService {
	private readonly insights = inject(InsightApiService);

	generate(analysisId: string, calculationId: string): Observable<Insight> {
		return this.insights.generateSummary(analysisId, calculationId).pipe(
			map((response) => response.insight),
			switchMap((insight) =>
				insight.status === 'pending'
					? followInsight(
							(id) =>
								this.insights.poll(
									analysisId,
									calculationId,
									id,
								),
							insight,
						)
					: of(insight),
			),
		);
	}

	load(
		analysisId: string,
		calculationId: string,
	): Observable<Insight | null> {
		return this.insights.lookupSummary(analysisId, calculationId).pipe(
			switchMap(({ insight }) => {
				if (!insight || !isPendingInsight(insight)) {
					return of(insight);
				}
				if (!isPollableInsight(insight)) return of(null);
				return followInsight(
					(id) => this.insights.poll(analysisId, calculationId, id),
					insight,
				);
			}),
		);
	}
}
